import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';

interface BoardItem {
  id: string;
  name_snapshot: string;
  quantity: number;
  notes: string | null;
  kitchen_status: string;
  tableLabel: string;
}

const COLUMNS = ['queued', 'in_prep', 'ready'] as const;

export function OrderBoard({ station, titleKey }: { station: 'cozinha' | 'bar'; titleKey: string }) {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const [items, setItems] = useState<BoardItem[]>([]);
  const wakeRef = useRef<WakeLockSentinel | null>(null);

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('order_items')
      .select('id, name_snapshot, quantity, notes, kitchen_status, orders!inner(approval_status), tabs(table_sessions(tables(label)))')
      .eq('restaurant_id', restaurant.id)
      .eq('station', station)
      .eq('orders.approval_status', 'approved')
      .in('kitchen_status', ['queued', 'in_prep', 'ready'])
      .order('created_at');
    if (data) {
      setItems(
        (data as unknown as Array<Record<string, unknown>>).map((it) => {
          const tab = it.tabs as { table_sessions?: { tables?: { label?: string } } } | null;
          return {
            id: it.id as string,
            name_snapshot: it.name_snapshot as string,
            quantity: it.quantity as number,
            notes: (it.notes as string | null) ?? null,
            kitchen_status: it.kitchen_status as string,
            tableLabel: tab?.table_sessions?.tables?.label ?? '—'
          };
        })
      );
    }
  }, [restaurant, station]);

  useEffect(() => {
    void reload();
    if (!restaurant) return;
    const ch = supabase
      .channel(`board-${station}-${restaurant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [restaurant, station, reload]);

  useEffect(() => {
    async function acquire() {
      try {
        if ('wakeLock' in navigator) wakeRef.current = await navigator.wakeLock.request('screen');
      } catch { /* sem suporte */ }
    }
    void acquire();
    const onVis = () => { if (document.visibilityState === 'visible') void acquire(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      void wakeRef.current?.release();
      wakeRef.current = null;
    };
  }, []);

  async function setStatus(id: string, status: string) {
    await supabase.from('order_items').update({ kitchen_status: status }).eq('id', id);
    void reload();
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page page--wide">
      <h1 className="page__title">{t(titleKey)}</h1>
      <div className="kds-board">
        {COLUMNS.map((col) => {
          const list = items.filter((i) => i.kitchen_status === col);
          return (
            <div key={col} className="kds-col">
              <h2 className="kds-col__title">
                {t(`kitchen.${col}`)} <span className="tag">{list.length}</span>
              </h2>
              {list.map((it) => (
                <div key={it.id} className="kds-card">
                  <div className="kds-card__head">
                    <strong>{t('kitchen.table')} {it.tableLabel}</strong>
                  </div>
                  <div className="kds-card__body">
                    {it.quantity}× {it.name_snapshot}
                    {it.notes && <div className="kds-card__notes">{it.notes}</div>}
                  </div>
                  <div className="kds-card__actions">
                    {col === 'queued' && (
                      <button type="button" className="btn btn--primary btn--sm" onClick={() => void setStatus(it.id, 'in_prep')}>
                        {t('kitchen.start')}
                      </button>
                    )}
                    {col === 'in_prep' && (
                      <button type="button" className="btn btn--primary btn--sm" onClick={() => void setStatus(it.id, 'ready')}>
                        {t('kitchen.done')}
                      </button>
                    )}
                    {col === 'ready' && (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void setStatus(it.id, 'served')}>
                        {t('kitchen.serve')}
                      </button>
                    )}
                    {col !== 'ready' && (
                      <button type="button" className="link-button" onClick={() => void setStatus(it.id, 'cancelled')}>
                        {t('kitchen.reject')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {list.length === 0 && <p className="muted-note">{t('kitchen.none')}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
